import React, { useEffect, useMemo, useRef } from 'react';
import { sanitizeEmailHtml, CID_ATTR } from '../../../lib/emails/emailHtml';
import { ladeCidBild } from '../../../lib/emails/cidBilder';

interface EmailHtmlAnsichtProps {
  html: string;
  emailId: string;
}

// Höchstens so viele Inline-Bilder pro Mail nachladen (je Bild bis zu 3 Graph-Calls).
const MAX_CID_BILDER = 20;

// Sanitisierter E-Mail-HTML-Inhalt; eingebettete cid:-Bilder werden nach dem Rendern
// asynchron aus Graph nachgeladen und als Blob-URLs eingesetzt.
const EmailHtmlAnsicht: React.FC<EmailHtmlAnsichtProps> = ({ html, emailId }) => {
  const { html: sauber, cids } = useMemo(() => sanitizeEmailHtml(html), [html]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cids.length === 0) return;
    let abgebrochen = false;
    const urls: string[] = [];

    (async () => {
      for (const cid of cids.slice(0, MAX_CID_BILDER)) {
        try {
          const blob = await ladeCidBild(emailId, cid);
          if (abgebrochen) return;
          if (!blob.type.startsWith('image/')) continue; // octet-stream = kein sicheres Bild
          const url = URL.createObjectURL(blob);
          urls.push(url);
          containerRef.current
            ?.querySelectorAll(`img[${CID_ATTR}="${CSS.escape(cid)}"]`)
            .forEach((img) => img.setAttribute('src', url));
        } catch {
          // Kein harter Fehler fuer die Mail — aber auch kein stummer: ein leerer Fleck hat
          // den Abruf-Fehler aus Ticket #495 monatelang versteckt. Der Alt-Text erscheint
          // anstelle des Bildes.
          containerRef.current
            ?.querySelectorAll(`img[${CID_ATTR}="${CSS.escape(cid)}"]`)
            .forEach((img) => {
              img.setAttribute('alt', 'Eingebettetes Bild konnte nicht geladen werden');
              img.setAttribute('title', 'Eingebettetes Bild konnte nicht geladen werden');
            });
        }
      }
    })();

    return () => {
      abgebrochen = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [emailId, cids]);

  return (
    <div
      ref={containerRef}
      className="prose prose-sm max-w-full overflow-x-auto rounded-xl bg-white border border-gray-100 p-4 text-gray-700 break-words
        [&_img]:max-w-full [&_img]:h-auto [&_table]:text-xs [&_a]:text-[#1a1d24] [&_a]:underline"
      dangerouslySetInnerHTML={{ __html: sauber }}
    />
  );
};

export default EmailHtmlAnsicht;
